import { Card } from 'components/Card';
import { CardGeneric } from 'components/CardGeneric';
import { TextAreaField } from 'components/Field/TextAreaField/TextAreaField';
import {
  ModalScreenProps,
  ModalScreens,
  ScreenProps,
  Screens,
  Stacks,
} from 'components/Navigation/types';
import { Page } from 'components/Page';
import { Txt } from 'components/Txt';
import { useScreenSize } from 'hooks/useScreenSize';
import { useSelector } from 'hooks/useSelector';
import { filterArrayEmpty } from 'lib/riskbase';
import { pluralise } from 'lib/pluralise';
import { cloneDeep, isEqual } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import { AddCustomAction } from 'screens/AssessmentScreen/Explorer/ExplorerActionRow';
import { AddCustomControl } from 'screens/AssessmentScreen/Explorer/ExplorerControlRow';
import { doAseUpdateSection } from 'store/assessments/actions';
import {
  selectAseSectionActionIds,
  selectAseSectionControlIds,
  selectAseSection,
  selectAseTplSection,
  selectAseTemplateSettings,
} from 'store/assessments/selectors';
import { SectionResult } from 'store/assessments/types';
import { useEsc } from 'hooks/useEsc';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { NAVIGATE_MODAL_BACK } from 'store/actions';
import { CardRow } from 'components/CardRow';
import { PageHeader } from 'components/PageHeader';
import { View } from 'react-native';
import { GapSmall } from 'components/GapSmall';

export const SectionEdit: React.FC<
  | ModalScreenProps<ModalScreens.AseSection>
  | ScreenProps<Stacks.ASSESSMENT, Screens.ASE_SECTION>
> = ({ navigation, route, isModal }) => {
  const dispatch = useDispatch();
  const confirmUnsaved = useConfirmUnsaved();
  const screenSize = useScreenSize();
  const forceUpdate = useForceUpdate();
  const { id } = route.params;

  const template = useSelector((state) => selectAseTplSection(state, id));
  const selectedData = useSelector((state) => selectAseSection(state, id));
  const templateSettings = useSelector(selectAseTemplateSettings);

  const dataRef = React.useRef<SectionResult>(
    cloneDeep(selectedData || { id, comment: '' }),
  );
  const initDataRef = React.useRef<SectionResult>(cloneDeep(dataRef.current));
  const internalSave = !!isModal;

  if (!internalSave && selectedData) {
    dataRef.current = selectedData;
    initDataRef.current = dataRef.current;
  }

  const section: SectionResult | null = internalSave
    ? dataRef.current!
    : selectedData;

  const actionsCount = useSelector(
    (state) => selectAseSectionActionIds(state, id),
    isEqual,
  ).length;
  const controlsCount = useSelector(
    (state) => selectAseSectionControlIds(state, id),
    isEqual,
  ).length;

  const isDirtyRef = React.useRef(false);
  const checkIsDirty = React.useCallback(
    () => !isEqual(initDataRef.current, dataRef.current),
    [],
  );
  isDirtyRef.current = checkIsDirty();

  const handleHardSave = React.useCallback(() => {
    if (checkIsDirty()) {
      dispatch(doAseUpdateSection({ id, data: dataRef.current }));
    } else if (isModal) {
      dispatch(NAVIGATE_MODAL_BACK());
    } else if (screenSize === 'small') {
      navigation?.goBack();
    }
  }, [checkIsDirty, dispatch, id, isModal, navigation, screenSize]);

  const handleClose = React.useCallback(() => {
    if (isModal) {
      if (checkIsDirty()) {
        confirmUnsaved({
          onSave: handleHardSave,
          onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
        });
        return;
      }
      dispatch(NAVIGATE_MODAL_BACK());
    } else if (screenSize === 'small') {
      navigation?.goBack();
    }
  }, [
    checkIsDirty,
    confirmUnsaved,
    dispatch,
    handleHardSave,
    isModal,
    navigation,
    screenSize,
  ]);

  const handleSoftSave = React.useCallback(
    (
      changes: Partial<SectionResult>,
      { doForceUpdate }: { doForceUpdate?: 'always' | 'dirtyChange' },
    ) => {
      const newValues = { ...dataRef.current, ...changes };
      if (!isEqual(dataRef.current, newValues)) {
        if (internalSave) {
          dataRef.current = { ...dataRef.current, ...changes };
          if (
            doForceUpdate === 'always' ||
            (doForceUpdate === 'dirtyChange' &&
              checkIsDirty() !== isDirtyRef.current)
          ) {
            forceUpdate();
          }
        } else {
          dispatch(doAseUpdateSection({ id, data: newValues }));
        }
      }
    },
    [checkIsDirty, dispatch, forceUpdate, id, internalSave],
  );

  const saveField = React.useCallback(
    (name: keyof SectionResult, value: string) =>
      handleSoftSave({ [name]: value }, { doForceUpdate: 'dirtyChange' }),
    [handleSoftSave],
  );

  const isDirtyVar = checkIsDirty();
  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirtyVar });
  }, [navigation, isDirtyVar]);
  useEsc(isModal && handleClose);

  return (
    <Page
      scrollView
      header={
        <PageHeader
          title="Section"
          onPressSaveButton={isModal ? handleHardSave : undefined}
          onPressCancelButton={isModal ? handleClose : undefined}
          onPressBackButton={
            !isModal && screenSize === 'small' ? navigation?.goBack : undefined
          }
        />
      }
    >
      <CardGeneric>
        <Txt size="large" weight="bold">
          {template.name}
        </Txt>
        {!!template.description && <Txt>{template.description}</Txt>}
      </CardGeneric>
      <Card
        label="Remarks"
        explanation={
          (!!actionsCount || !!controlsCount) &&
          `${[
            pluralise(actionsCount, '1 action', '{x} actions', ''),
            pluralise(controlsCount, '1 control', '{x} controls', ''),
          ]
            .filter(filterArrayEmpty)
            .join(' and ')} added to section`
        }
      >
        <TextAreaField
          placeholder="No Remarks"
          value={section?.comment || ''}
          onChangeWithName={saveField}
          name="comment"
          onEsc={handleClose}
          autoFocusWeb={isModal}
          autoFocusDelay
        />
      </Card>

      {(templateSettings.addCustomActions ||
        templateSettings.addCustomControls) && (
        <View>
          <Card
            label={`Add ${[
              templateSettings.addCustomActions ? 'Actions' : null,
              templateSettings.addCustomControls ? 'Controls' : null,
            ]
              .filter(filterArrayEmpty)
              .join(' and ')}`}
          />
          {isModal ? (
            <Card>
              <CardRow
                value={`Close this window to add ${[
                  templateSettings.addCustomActions ? 'actions' : null,
                  templateSettings.addCustomControls ? 'controls' : null,
                ]
                  .filter(filterArrayEmpty)
                  .join(' and ')}`}
              />
            </Card>
          ) : (
            <GapSmall>
              {templateSettings.addCustomActions && (
                <Card>
                  <AddCustomAction
                    parentId={id}
                    parentType="section"
                    fieldInstanceId={null}
                    parent={null}
                  />
                </Card>
              )}
              {templateSettings.addCustomControls && (
                <Card>
                  <AddCustomControl
                    parentId={id}
                    parentType="section"
                    fieldInstanceId={null}
                    parent={null}
                  />
                </Card>
              )}
            </GapSmall>
          )}
        </View>
      )}
    </Page>
  );
};
